import {ResourceCategory} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ServiceStatus} from "crqlar-react-common/dist/models/service/serviceStatus.model";

/**
 * Why a service can't be saved as a tip service: guests could book it, it is linked to resources
 * that aren't staff, or another one is already the default tip service of one of its sectors, or
 * already has some of its staff.
 */
export type TipServiceConflict =
    | {kind: 'BOOKABLE'}
    | {kind: 'NOT_STAFF', resourceIds: string[]}
    | {kind: 'DEFAULT', other: Service}
    | {kind: 'STAFF', other: Service, resourceIds: string[]};

// The backend's staff categories (DigitalSigningContextResolver.isStaffCategory), by name - the shared
// collectStaffCategoryIds knows fewer names, so a tip service would offer less than the backend accepts
const STAFF_CATEGORY_MARKERS = ['staff', 'team', 'employee', 'personal', 'mitarbeiter'];

export const isStaffCategory = (category: Pick<ResourceCategory, 'name' | 'nameLocalized'>) => (
    [category.name, ...Object.values(category.nameLocalized ?? {})]
        .map(name => (name ?? '').trim().toLowerCase())
        .some(name => STAFF_CATEGORY_MARKERS.some(marker => name.includes(marker)))
);

/** Only a service guests can't book can take tips, or one that already does (so it can be turned off). */
export const canBeTipService = (service: Service) => !!service.tipService || service.status !== ServiceStatus.ACTIVE;

const idsOf = (ids?: string[]) => (ids ?? []).filter((id, index, all) => !!id?.trim() && all.indexOf(id) === index);

/**
 * The tip services' rule as the backend checks it before a save (ServiceServiceV3.validateTipService):
 * a tip service can't be bookable and takes the tips of staff only - its linked resources; a sector has
 * at most one default tip service - linked to no staff - and any number linked to staff, each staff
 * member with at most one tip service per sector. A signing posts the tip to the treating
 * staff member's own, else to the default one. [isStaff] tells a linked resource's kind, undefined when
 * it isn't known here. Undefined when [service] may be saved.
 */
export const tipServiceConflict = (
    service: Service,
    services: Service[],
    isStaff: (resourceId: string) => boolean | undefined = () => undefined
): TipServiceConflict | undefined => {
    if (!service.tipService || service.archived) return undefined;
    // Without a status it is saved as Active (prepareServiceForSave)
    if ((service.status ?? ServiceStatus.ACTIVE) === ServiceStatus.ACTIVE) return {kind: 'BOOKABLE'};
    const staffIds = idsOf(service.resourceIds);
    const notStaff = staffIds.filter(id => isStaff(id) === false);
    if (notStaff.length) return {kind: 'NOT_STAFF', resourceIds: notStaff};

    const sectorIds = idsOf(service.sectorIds);
    if (!sectorIds.length) return undefined;
    const others = services.filter(other => (
        !!other.tipService && !other.archived && other.id !== service.id
        && (other.sectorIds ?? []).some(sectorId => sectorIds.includes(sectorId))
    ));
    if (!staffIds.length) {
        const defaultTip = others.find(other => !idsOf(other.resourceIds).length);
        return defaultTip && {kind: 'DEFAULT', other: defaultTip};
    }
    for (const other of others) {
        const shared = staffIds.filter(id => (other.resourceIds ?? []).includes(id));
        if (shared.length) return {kind: 'STAFF', other, resourceIds: shared};
    }
    return undefined;
};
